import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Upload, FileSpreadsheet, Loader2, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkStudentUpload = ({ onComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/teacher/students/csv-template`);
      const blob = new Blob([res.data.content], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename || 'students_template.csv';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a .csv file');
      return;
    }
    setFile(f);
    setResults(null);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file first');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const res = await axios.post(`${API}/teacher/students/upload-csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResults(res.data);
      if (res.data.failed === 0) {
        toast.success(`All ${res.data.success} students added successfully!`);
      } else {
        toast.warning(`${res.data.success} added, ${res.data.failed} failed — review below`);
      }
      // NOTE: Don't call onComplete here — it triggers a parent loading state that
      // unmounts this component and wipes `results` (auto-generated passwords would
      // be lost forever). We invoke it from resetForm() instead, after the teacher
      // has downloaded their credentials.
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadResults = () => {
    if (!results) return;
    const headers = 'row,name,username,password,roll_number,class_level,status,error';
    const rows = results.results.map(r => [
      r.row || '',
      r.name || '',
      r.username || '',
      r.password || '',
      r.roll_number || '',
      r.class_level || '',
      r.status || '',
      (r.error || '').replace(/,/g, ';'),
    ].join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `student_upload_results_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    // Now that the teacher is done with the results, refresh the student directory.
    if (onComplete) onComplete();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <FileSpreadsheet className="text-green-600" size={20} />
          Bulk Add Students (CSV Upload)
        </h3>
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-csv-template">
          <Download size={16} className="mr-1" />
          Download Template
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 mb-4">
        <p className="font-semibold mb-1">CSV format:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Required columns: <code className="bg-white px-1 rounded">name</code>, <code className="bg-white px-1 rounded">roll_number</code>, <code className="bg-white px-1 rounded">class_level</code></li>
          <li>Optional: <code className="bg-white px-1 rounded">username</code> (auto-generated if blank), <code className="bg-white px-1 rounded">password</code> (auto-generated if blank)</li>
          <li><code className="bg-white px-1 rounded">class_level</code> must be 6, 7, 8, 9 or 10</li>
          <li>All students will be added to <strong>your school</strong>.</li>
        </ul>
      </div>

      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-100 file:text-blue-700 file:font-medium hover:file:bg-blue-200 cursor-pointer"
          data-testid="csv-file-input"
        />
        <Button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="bg-green-600 hover:bg-green-700 shrink-0"
          data-testid="upload-csv-btn"
        >
          {uploading ? <Loader2 size={16} className="animate-spin mr-1" /> : <Upload size={16} className="mr-1" />}
          {uploading ? 'Uploading...' : 'Upload CSV'}
        </Button>
      </div>

      {/* Results */}
      {results && (
        <div className="mt-5 border rounded-lg overflow-hidden">
          <div className="p-3 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 text-sm">
              <Badge variant="default" className="bg-green-600">{results.success} succeeded</Badge>
              {results.failed > 0 && <Badge variant="destructive">{results.failed} failed</Badge>}
              <span className="text-gray-500">Total rows: {results.total}</span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={downloadResults} data-testid="download-results-btn">
                <Download size={14} className="mr-1" />
                Download Results
              </Button>
              <Button size="sm" variant="outline" onClick={resetForm} data-testid="reset-csv-btn">
                Upload Another
              </Button>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Row</th>
                  <th className="px-2 py-1 text-left">Name</th>
                  <th className="px-2 py-1 text-left">Username</th>
                  <th className="px-2 py-1 text-left">Password</th>
                  <th className="px-2 py-1 text-left">Roll</th>
                  <th className="px-2 py-1 text-left">Class</th>
                  <th className="px-2 py-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {results.results.map((r, i) => (
                  <tr key={i} className={r.status === 'success' ? 'bg-green-50' : 'bg-red-50'}>
                    <td className="px-2 py-1 text-gray-500">{r.row || '-'}</td>
                    <td className="px-2 py-1 font-medium">{r.name || '-'}</td>
                    <td className="px-2 py-1 font-mono">{r.username || '-'}</td>
                    <td className="px-2 py-1 font-mono">{r.password || '-'}</td>
                    <td className="px-2 py-1">{r.roll_number || '-'}</td>
                    <td className="px-2 py-1">{r.class_level || '-'}</td>
                    <td className="px-2 py-1">
                      {r.status === 'success' ? (
                        <span className="text-green-700 inline-flex items-center gap-1">
                          <CheckCircle size={12} /> Added
                        </span>
                      ) : (
                        <span className="text-red-700 inline-flex items-center gap-1" title={r.error}>
                          <XCircle size={12} /> {r.error}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {results.success > 0 && (
            <div className="p-3 bg-yellow-50 border-t border-yellow-200 text-xs text-yellow-900">
              <AlertCircle size={14} className="inline mr-1" />
              <strong>Important:</strong> Auto-generated passwords are shown only once. Download the results CSV now to share with students.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkStudentUpload;
